import {JSX, useEffect, useRef, useState } from "react";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import * as _ from "lodash-es";
import { FormattedNumber } from "react-intl";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import * as d3 from "d3";
import OwSortHeader from "../../../shared/OwSortHeader/OwSortHeader";
import ExpandResultsBar from "../../../shared/ExpandResultsBar/ExpandResultsBar";
import SortOrderEnum from "../../../../models/SortOrderEnum";
import UIService from "../../../../services/UIService";
import ExcelJS from 'exceljs';
import moment from "moment-timezone";
import { ReportSalesByUserResponseDTO } from "../../../../models/DTO/ReportSalesByUserResponseDTO";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import ISupplierModel from "../../../../models/ISupplierModel";
import IVendorModel from "../../../../models/IVendorModel";
import IUser from "../../../../models/IUser";

type Props = {
    supplierModel: ISupplierModel;
    vendorModel: IVendorModel;
    userModel: IUser;
    processedReport: IProcessed_Report;
};

const ReportSalesByUser = (props: Props) => {
    const [reportData, setReportData] = useState<ReportSalesByUserResponseDTO>({} as ReportSalesByUserResponseDTO);
    const [userTable, setUserTable] = useState<JSX.Element | null>(null);
    const [productsTable, setProductsTable] = useState<JSX.Element | null>(null);
    const [orderIssues, setOrderIssues] = useState<any[]>([]);
    const [showAllProducts, setShowAllProducts] = useState<boolean>(false);
    const [sortCol, setSortCol] = useState<string>("qty");
    const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.DESC);
    const [users, setUsers] = useState<any[] | undefined>(undefined);
    const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
    const [totalOrders, setTotalOrders] = useState<number | undefined>(undefined);
    const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
    const [products, setProducts] = useState<any[] | undefined>(undefined);
    const [periodStart, setPeriodStart] = useState<any | undefined>(undefined);
    const [periodEnd, setPeriodEnd] = useState<any | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const salesChartRef = useRef<SVGSVGElement>(null);
    const productsChartRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        onGetReportSalesByUser(props.processedReport.report_data);
    }, [props.processedReport.report_data]);

    // Render charts after component mounts and when data changes
    useEffect(() => {
        if (users && users.length && salesChartRef.current) {
            // Small delay to ensure DOM is fully rendered
            setTimeout(() => {
                renderSalesByUserPieChart();
            }, 50);
        }

        if (products && products.length && productsChartRef.current) {
            setTimeout(() => {
                renderSalesByProductPieChart();
            }, 50);
        }
    }, [users, products, salesChartRef.current, productsChartRef.current]);

    // Handle window resize
    useEffect(() => {
        const handleResize = _.debounce(() => {
            if (users && users.length) {
                renderSalesByUserPieChart();
            }
            if (products && products.length) {
                renderSalesByProductPieChart();
            }
        }, 300);

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [users, products]);

    const downloadExcel = () => {
        if (reportData) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet("Commissions");

            const salesPeopleWs: { salesperson: string; ws: ExcelJS.Worksheet }[] = [];

            ws.columns = [
                { header: "Salesperson", key: "salesperson" },
                { header: "Email", key: "email" },
                { header: "Total Orders", key: "totalOrders" },
                { header: "Amount", key: "amount" },
                { header: "Average Sale", key: "averageSale" },
            ];

            const boldHeaders = ["A1", "B1", "C1", "D1", "E1"];

            for (const header of boldHeaders) {
                ws.getCell(header).font = {
                    bold: true,
                };
            }

            const userRows = Object.keys(reportData?.users);

            if (reportData?.users) {
                for (const user of reportData.users) {
                    const { name, email, totalOrders, amount } = user;

                    const averageSale = user.amount / user.totalOrders;

                    ws.addRow({ salesperson: name, email, totalOrders, amount, averageSale });
                }
            }

            const productWs = workbook.addWorksheet("Products");

            productWs.columns = [
                { header: "Name", key: "name" },
                { header: "Qty", key: "qty" },
                { header: "Amount", key: "amount" },
            ];

            const productBoldHeaders = ["A1", "B1", "C1"];

            for (const header of productBoldHeaders) {
                productWs.getCell(header).font = {
                    bold: true,
                };
            }

            if (reportData?.products) {
                for (const product of reportData.products) {
                    const { name, qty, amount } = product;

                    productWs.addRow({ name, qty, amount });
                }
            }

            const periodStartFormatted = moment(reportData.periodStart).format("YYYY-MM-DD");
            const periodEndFormatted = moment(reportData.periodEnd).format("YYYY-MM-DD");

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `${periodStartFormatted}-${periodEndFormatted}-orderwerks-sales-volume-by-user-report.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    const onGetReportSalesByUser = (data: ReportSalesByUserResponseDTO) => {
        const { periodStart, periodEnd, products, totalAmount, totalOrders, users, ordersWithIssues, totalItems } = data;

        const orderIssuesArr: { name: string; message: string; guid: string; user: string }[] = [];

        if (ordersWithIssues) {
            const ordersWithIssuesGuids = Object.keys(ordersWithIssues);

            for (const orderGuid of ordersWithIssuesGuids) {
                const details = ordersWithIssues[orderGuid];

                orderIssuesArr.push({
                    ...details,
                    guid: orderGuid,
                });
            }
        }

        setReportData(data);
        setPeriodStart(periodStart);
        setPeriodEnd(periodEnd);
        setProducts(products);
        setTotalAmount(totalAmount);
        setTotalOrders(totalOrders);
        setUsers(users);
        setTotalItems(totalItems);
        setOrderIssues(orderIssuesArr);
        setIsLoading(false);

        setUserTable(getUserTableReport(users, totalAmount, totalOrders));
        setProductsTable(getProductsTableReport(products, sortCol, sortOrder, showAllProducts));
    };

    const getUserTableReport = (usersData = users, totalAmountVal = totalAmount, totalOrdersVal = totalOrders) => {
        if (!usersData) return null;

        const tableHeader = (
            <tr>
                <th>
                    <OwLabel title={"name"} />
                </th>
                <th>
                    <OwLabel title={"email"} />
                </th>
                <th className={"text-end"}>
                    <OwLabel title={"total_orders"} />
                </th>
                <th className={"text-end"}>
                    <OwLabel title={"amount"} />
                </th>
                <th className={"text-end"}>
                    <OwLabel title={"average_sale"} />
                </th>
            </tr>
        );

        const tableRows = _.map(usersData, (user, index) => {
            const average_sale = user.amount / user.totalOrders;

            return (
                <tr key={index}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td className={"text-end"}>{user.totalOrders}</td>
                    <td className={"text-end"}>
                        <FormattedNumber value={user.amount} maximumFractionDigits={2} style={"currency"} currency={"USD"} />
                    </td>
                    <td className={"text-end"}>
                        <FormattedNumber value={average_sale} maximumFractionDigits={2} style={"currency"} currency={"USD"} />
                    </td>
                </tr>
            );
        });

        let average_sale_all_users = 0;

        if (totalAmountVal && totalOrdersVal) {
            average_sale_all_users = totalAmountVal / totalOrdersVal;
        }

        if (_.isNaN(average_sale_all_users)) {
            average_sale_all_users = 0.0;
        }

        const totalsRow = (
            <tr>
                <td />
                <td />
                <td className={"text-end"}>{totalOrdersVal}</td>
                <td className={"text-end"}>
                    <FormattedNumber value={totalAmountVal!} maximumFractionDigits={2} style={"currency"} currency={"USD"} />
                </td>
                <td className={"text-end"}>
                    <FormattedNumber value={average_sale_all_users} maximumFractionDigits={2} style={"currency"} currency={"USD"} />
                </td>
            </tr>
        );

        let tableClass = "table-dark";

        if (UIService.isMainViewLight()) {
            tableClass = "";
        }

        return (
            <div className={"table-responsive"}>
                <table className={"table " + tableClass}>
                    <thead>{tableHeader}</thead>
                    <tbody>
                    {tableRows}
                    {totalsRow}
                    </tbody>
                </table>
            </div>
        );
    };

    const setSort = (col: string, order: SortOrderEnum) => {
        setSortCol(col);
        setSortOrder(order);
        setProductsTable(getProductsTableReport(products, col, order, showAllProducts));
    };

    const expandedResults = (showExpanded: boolean) => {
        setShowAllProducts(showExpanded);
        setProductsTable(getProductsTableReport(products, sortCol, sortOrder, showExpanded));
    };

    const getProductsTableReport = (productsData = products, sortColumn = sortCol, sortDirection = sortOrder, showAll = showAllProducts) => {
        if (!productsData) return null;

        const tableHeader = (
            <tr>
                <th>
                    <OwSortHeader
                        title={"name"}
                        currentSortCol={sortColumn}
                        currentSortOrder={sortDirection}
                        sortFn={setSort}
                    />
                </th>
                <th className={"w-15 text-end"}>
                    <OwSortHeader
                        title={"qty"}
                        currentSortCol={sortColumn}
                        currentSortOrder={sortDirection}
                        sortFn={setSort}
                    />
                </th>
                <th className={"w-20 text-end"}>
                    <OwSortHeader
                        title={"amount"}
                        currentSortCol={sortColumn}
                        currentSortOrder={sortDirection}
                        sortFn={setSort}
                    />
                </th>
            </tr>
        );

        let productsToDisplay = _.orderBy(productsData, [sortColumn], [sortDirection]);

        if (!showAll) {
            productsToDisplay = _.take(productsToDisplay, 10);
        }

        const tableRows = _.map(productsToDisplay, (product: any, index: number) => {
            return (
                <tr key={index}>
                    <td>{product.name}</td>
                    <td className={"text-end"}>
                        <FormattedNumber value={product.qty} />
                    </td>
                    <td className={"text-end"}>
                        <FormattedNumber value={product.amount} maximumFractionDigits={2} style={"currency"} currency={"USD"} />
                    </td>
                </tr>
            );
        });

        let tableClass = "table-dark";

        if (UIService.isMainViewLight()) {
            tableClass = "";
        }

        return (
            <div className={"mb-5 table-responsive"}>
                <table className={"table " + tableClass}>
                    <tbody>
                    {tableHeader}
                    {tableRows}
                    </tbody>
                </table>
                <div className={productsData && productsData.length > 10 ? "" : "d-none"}>
                    <ExpandResultsBar expandedFn={expandedResults} />
                </div>
            </div>
        );
    };

    const renderSalesByUserPieChart = () => {
        if (!salesChartRef.current || !users || !users.length) return;

        // Clear previous chart
        d3.select(salesChartRef.current).selectAll("*").remove();

        // Get the actual width of the container
        const containerWidth = salesChartRef.current.parentElement?.clientWidth || 400;
        const containerHeight = 400;

        // If container has no width, try again later
        if (containerWidth <= 0) {
            setTimeout(renderSalesByUserPieChart, 100);
            return;
        }

        // Set dimensions with margins
        const margin = { top: 40, right: 20, bottom: 20, left: 20 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;
        const radius = Math.min(width, height) / 2;

        // Set SVG dimensions
        d3.select(salesChartRef.current)
            .attr("width", containerWidth)
            .attr("height", containerHeight);

        // Create a group for the pie chart, centered in the SVG
        const svg = d3.select(salesChartRef.current)
            .append("g")
            .attr("transform", `translate(${containerWidth / 2}, ${containerHeight / 2})`);

        // Add title
        svg.append("text")
            .attr("x", 0)
            .attr("y", -height / 2 + 10)
            .attr("text-anchor", "middle")
            .style("font-size", "16px")
            .text("Sales By User");

        // Prepare the data for D3
        const pieData = users.map(user => ({
            name: user.name,
            value: user.amount
        }));

        // Color scale
        const color = d3.scaleOrdinal(d3.schemeCategory10);

        // Create pie layout
        const pie = d3.pie<any>()
            .value(d => d.value)
            .sort(null);

        // Create arc generator
        const arc = d3.arc<any>()
            .innerRadius(0)
            .outerRadius(radius * 0.8);

        // Create arc for labels
        const outerArc = d3.arc<any>()
            .innerRadius(radius * 0.9)
            .outerRadius(radius * 0.9);

        // Draw pie slices
        const slices = svg.selectAll(".slice")
            .data(pie(pieData))
            .enter()
            .append("g")
            .attr("class", "slice");

        slices.append("path")
            .attr("d", arc)
            .attr("fill", (d, i) => color(i.toString()))
            .attr("stroke", "white")
            .style("stroke-width", "2px");

        // Add labels with connector lines
        slices.append("polyline")
            .attr("points", function(d) {
                const pos = outerArc.centroid(d);
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                pos[0] = radius * 0.95 * (midAngle < Math.PI ? 1 : -1);

                // Convert the array of coordinate pairs to a string
                const pointsArray = [arc.centroid(d), outerArc.centroid(d), pos];
                return pointsArray.map(point => point[0] + ',' + point[1]).join(' ');
            })
            .style("fill", "none")
            .style("stroke", "black")
            .style("stroke-width", "1px");

        slices.append("text")
            .attr("transform", function(d) {
                const pos = outerArc.centroid(d);
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                pos[0] = radius * (midAngle < Math.PI ? 1.05 : -1.05);
                return `translate(${pos})`;
            })
            .attr("dy", ".35em")
            .style("text-anchor", function(d) {
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                return midAngle < Math.PI ? "start" : "end";
            })
            .text(d => d.data.name);

        // Add percentage labels inside larger slices
        slices.filter(d => (d.endAngle - d.startAngle) > 0.2)
            .append("text")
            .attr("transform", d => `translate(${arc.centroid(d)})`)
            .attr("text-anchor", "middle")
            .attr("dy", ".35em")
            .style("fill", "white")
            .style("font-size", "12px")
            .text(d => {
                const percent = Math.round((d.data.value / d3.sum(pieData, d => d.value)) * 100);
                return `${percent}%`;
            });
    };

    const renderSalesByProductPieChart = () => {
        if (!productsChartRef.current || !products || !products.length) return;

        // Clear previous chart
        d3.select(productsChartRef.current).selectAll("*").remove();

        // Get the actual width of the container
        const containerWidth = productsChartRef.current.parentElement?.clientWidth || 400;
        const containerHeight = 400;

        // If container has no width, try again later
        if (containerWidth <= 0) {
            setTimeout(renderSalesByProductPieChart, 100);
            return;
        }

        // Set dimensions with margins
        const margin = { top: 40, right: 20, bottom: 20, left: 20 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;
        const radius = Math.min(width, height) / 2;

        // Set SVG dimensions
        d3.select(productsChartRef.current)
            .attr("width", containerWidth)
            .attr("height", containerHeight);

        // Create a group for the pie chart, centered in the SVG
        const svg = d3.select(productsChartRef.current)
            .append("g")
            .attr("transform", `translate(${containerWidth / 2}, ${containerHeight / 2})`);

        // Add title
        svg.append("text")
            .attr("x", 0)
            .attr("y", -height / 2 + 10)
            .attr("text-anchor", "middle")
            .style("font-size", "16px")
            .text("Sales By Product");

        // Prepare the data for D3
        const pieData = products.map(product => ({
            name: product.name,
            value: product.amount
        }));

        // Sort data by value for better visualization
        pieData.sort((a, b) => b.value - a.value);

        // Color scale
        const color = d3.scaleOrdinal(d3.schemeCategory10);

        // Create pie layout
        const pie = d3.pie<any>()
            .value(d => d.value)
            .sort(null);

        // Create arc generator
        const arc = d3.arc<any>()
            .innerRadius(0)
            .outerRadius(radius * 0.8);

        // Create arc for labels
        const outerArc = d3.arc<any>()
            .innerRadius(radius * 0.9)
            .outerRadius(radius * 0.9);

        // Draw pie slices
        const slices = svg.selectAll(".slice")
            .data(pie(pieData))
            .enter()
            .append("g")
            .attr("class", "slice");

        slices.append("path")
            .attr("d", arc)
            .attr("fill", (d, i) => color(i.toString()))
            .attr("stroke", "white")
            .style("stroke-width", "2px");

        // Identify which slices should have labels
        // For products with many items, show only top products or those with significant slice size
        const maxLabels = 8; // Maximum number of labels to show
        const showLabels = slices
            .filter((d, i) => i < maxLabels || (d.endAngle - d.startAngle) > 0.2)
            .nodes();

        const showLabelIndices = new Set(
            showLabels.map(node => {
                const d = d3.select(node).data()[0] as any; // or a more specific type if you know it
                return d.index;
            })
        );

        // Add labels with connector lines
        slices.filter((d, i) => showLabelIndices.has(i))
            .append("polyline")
            .attr("points", function(d) {
                const pos = outerArc.centroid(d);
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                pos[0] = radius * 0.95 * (midAngle < Math.PI ? 1 : -1);

                // Convert the array of points to a string format
                const points = [arc.centroid(d), outerArc.centroid(d), pos];
                return points.map(p => p.join(',')).join(' ');
            })
            .style("fill", "none")
            .style("stroke", "black")
            .style("stroke-width", "1px");

        slices.filter((d, i) => showLabelIndices.has(i))
            .append("text")
            .attr("transform", function(d) {
                const pos = outerArc.centroid(d);
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                pos[0] = radius * (midAngle < Math.PI ? 1.05 : -1.05);
                return `translate(${pos})`;
            })
            .attr("dy", ".35em")
            .style("text-anchor", function(d) {
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                return midAngle < Math.PI ? "start" : "end";
            })
            .text(d => {
                // Truncate long product names
                const name = d.data.name;
                return name.length > 15 ? name.substring(0, 12) + '...' : name;
            });

        // Add percentage labels inside larger slices
        slices.filter(d => (d.endAngle - d.startAngle) > 0.2)
            .append("text")
            .attr("transform", d => `translate(${arc.centroid(d)})`)
            .attr("text-anchor", "middle")
            .attr("dy", ".35em")
            .style("fill", "white")
            .style("font-size", "12px")
            .text(d => {
                const percent = Math.round((d.data.value / d3.sum(pieData, d => d.value)) * 100);
                return `${percent}%`;
            });
    };

    if (isLoading) {
        return (
            <div className={"text-center"}>
                <OwTitle title={"loading_report_data"} leftIconName={"fas fa-circle-notch fa-spin"} />
            </div>
        );
    }

    return (
        <div className="ReportSalesByUser mt-2 mb-2">
            <div>
                <OwLabel className={"me-3"} title={"excel"} leftIconClass={"fas fa-download"} fn={downloadExcel} />
            </div>
            <div>
                <b>
                    <OwLabel className={"me-2"} title={"report_period"} />
                </b>
                {periodStart} - {periodEnd}
                <div className={orderIssues.length ? "" : "d-none"}>
                    <div>
                        <OwLabel title={"report_issues"} leftIconClass={"fas fa-exclamation-triangle"} className={"text-warning"} />
                        <br />
                        <OwLabel title={"there_are_order_issues_report_not_completely_accurate"} />
                    </div>

                    <div>
                        {_.map(orderIssues, (issue, idx) => {
                            return (
                                <div key={idx}>
                                    <a href={`/orders/${issue.guid}/entry`}>{issue.name}</a> - <OwLabel title={issue.message} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div>
                <OwTitle title={"users"} tag={"h5"} />
                {userTable}

                {products && products.length ? (
                    <div>
                        <OwTitle
                            className={products && products.length >= 10 ? "" : "d-none"}
                            title={showAllProducts ? "products-all" : "products-top_10"}
                            tag={"h5"}
                        />
                    </div>
                ) : null}
                <OwTitle
                    className={products && products.length < 10 ? "" : "d-none"}
                    title={"products"}
                    tag={"h5"}
                />
                {productsTable}
            </div>

            <div className={"row"}>
                <div className={"col-12 col-md-6"}>
                    <OwTitle tag={"h5"} title={"users"} />
                    <div className="chart-container" style={{ width: "100%", height: "400px", position: "relative" }}>
                        <svg
                            ref={salesChartRef}
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "block"
                            }}
                        ></svg>
                    </div>
                </div>
                <div className={"col-12 col-md-6"}>
                    <OwTitle tag={"h5"} title={"products"} />
                    <div className="chart-container" style={{ width: "100%", height: "400px", position: "relative" }}>
                        <svg
                            ref={productsChartRef}
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "block"
                            }}
                        ></svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

ReportSalesByUser.defaultProps = {
    periodType: "last_month",
};

export default ReportSalesByUser;
